/**
 * ================================================================================================
 *  THE STEP-UP, COMPLETED WHERE IT WAS DEMANDED. **Section 3, 2026-10-25.**
 * ================================================================================================
 *
 * The Owner, on `/tenant/users`:
 *
 *     *"מוצגת דרישה לאימות נוסף, אך ״אימות זהות והמשך״ הוא טקסט שאינו לחיץ. אין דרך להמשיך."*
 *
 * It was text because there was nowhere for it to lead. `MfaChallengeScreen` consumes a reference
 * issued AT SIGN-IN and, reached without one, says there is no open challenge and sends the person
 * back to sign in. No route existed by which a signed-in person could present their authenticator —
 * so every `per_action` capability, `companies.assignment.manage` among them, became unreachable
 * minutes after sign-in. `auth.stepup.assert` is that route; this is its control.
 *
 * ── IT IS INLINE, AND THAT IS THE REQUIREMENT, NOT A PREFERENCE ─────────────────────────────────
 *
 *     *"חזרה למסך ולפעולה המקוריים לאחר הצלחה, תוך שמירת פרטים שהוזנו ככל שרלוונטי."*
 *
 * A navigation to a challenge screen and back cannot keep what somebody typed: the form unmounts,
 * its state goes with it, and "return to the original action" becomes "start again". Asking for the
 * code WHERE THE REFUSAL APPEARED keeps the screen mounted, so every field they had filled in is
 * still filled in — the requirement is met by not leaving rather than by restoring.
 *
 * On success it calls `onSatisfied`, and the screen retries the thing it was doing.
 *
 * ── WHAT IT DOES NOT DO ─────────────────────────────────────────────────────────────────────────
 *
 * It does not decide anything. The gate refused, the gate will decide again on the retry, and if
 * the assertion did not take then the retry is refused again — which is the correct direction. It
 * weakens no requirement: it is the first way to SATISFY one that was already being enforced.
 *
 * ── AND IT IS ASKED FOR IN THE MIDDLE OF THE SCREEN. **Section 5, 2026-10-27.** ─────────────────
 *
 * Owner: *"האימות הופיע ברצועה צרה בצד, בתוך מסגרות והסברים חוזרים, ודרש גלילה"*, and the
 * requirement: *"כרטיס או חלונית אימות ממורכזים, קומפקטיים וקריאים. הוראה קצרה, שדה קוד וכפתור
 * אישור; פירוט בעזרה לפי דרישה."*
 *
 * He is describing where the refusal lives. A refusal appears where the action was pressed — which
 * on a long screen is below the fold, inside a notice, inside a panel, three borders deep. The
 * code field inherited that position because it was rendered inside the refusal.
 *
 * So the prompt now presents itself as a CENTRED CARD over the screen, reusing the product's own
 * `.modal-scrim` / `.modal` presentation: one instruction, one field, one button. The refusal stays
 * underneath, unchanged, because the card is a layer rather than a replacement.
 *
 * DISMISSING IT COSTS NOTHING AND LOSES NOTHING. Escape or a click outside closes the card and
 * leaves a single button in its place — the screen, with every answer still typed into it, is
 * exactly where it was. That is the same rule the `Modal` component states as M5, which is why
 * this borrows its clothes: one decision, one field, nothing to retype.
 *
 * ── WHY IT CAN BE ASKED TWICE, AND WHY THAT IS NOT A LOOP ──────────────────────────────────────
 *
 * The product's step-up freshness is a server constant (`STEPUP_FRESHNESS_SECONDS` in
 * `packages/authn/src/mfa-policy.ts`), and a factor presented a while ago stops satisfying it. An
 * administrator who signs in, reads a screen and then spends some minutes filling a form can
 * therefore be asked again at the moment of the write. That is the control working.
 *
 * What it must never be is a MYSTERY, so the card says it plainly. The NUMBER is deliberately not
 * repeated here: a second copy of a security constant in the browser is the "one rule, two
 * implementations" defect this repository has already paid for once in this very control, where
 * `gate.ts` held 900 and the policy held 300.
 */

import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';

import { callOperation } from '../api/operations';
import { ApiError, ConflictError } from '../api/client';
import { routes } from '../routes';

/** 6 digits, as `TOTP_DIGITS` everywhere else in this product. */
const DIGITS = 6;

type Outcome =
  | { readonly kind: 'idle' }
  | { readonly kind: 'sending' }
  /**
   * THE FACTOR WAS ACCEPTED AND THE WRITE HAS NOT HAPPENED. **Section 6, 2026-10-28.**
   *
   * This state did not exist: on success the component called `onSatisfied` immediately and the
   * card vanished, so a security step that worked looked exactly like a press that did nothing.
   * The Owner pressed again and the process moved, and reported it as a defect -- correctly.
   */
  | { readonly kind: 'accepted' }
  /** The server refused, and will not say which of wrong / malformed / replayed it was. */
  | { readonly kind: 'refused' }
  /** No second factor on this account at all — a retry cannot help, enrolment can. */
  | { readonly kind: 'notEnrolled' }
  | { readonly kind: 'failed'; readonly requestUid: string };

export interface StepUpPromptProps {
  /** Run when the person closes the confirmed card: the screen clears the refusal. */
  readonly onSatisfied: () => void;
  /**
   * The label of the control that completes the action, when the screen has one.
   *
   * **Section 6, 2026-10-28.** *"אם לאחר האימות צריך לאשר שוב את ההזמנה, לומר זאת במפורש ולהציג
   * את הפעולה הנכונה."* Passing the actual button text means the sentence points at something the
   * person can see rather than describing it. Omitted, the card says the action can be repeated,
   * without naming a control this component cannot know.
   */
  readonly continueLabelHe?: string;
}

export function StepUpPrompt({ onSatisfied, continueLabelHe }: StepUpPromptProps): JSX.Element {
  const [code, setCode] = useState('');
  const [outcome, setOutcome] = useState<Outcome>({ kind: 'idle' });
  /** Closed by Escape or a click outside. Re-opened by the one button that replaces it. */
  const [open, setOpen] = useState(true);
  /** The long explanation, on request only. Section 5: *"פירוט בעזרה לפי דרישה."* */
  const [helpOpen, setHelpOpen] = useState(false);

  /*
   * ESCAPE CLOSES IT. Nothing is lost: the screen behind is mounted, the refusal is still there,
   * and the button that reopens the card is in its place.
   */
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  function submit(e: FormEvent): void {
    e.preventDefault();
    /*
     * ONE REQUEST AT A TIME. The button is disabled while a check is in flight, and a disabled
     * button stops a click and nothing else -- Enter in the code field submits the form. A second
     * assertion with the same code is refused by the server as a replay, which would turn a
     * successful authentication into a refusal in front of the person who just typed it.
     */
    if (outcome.kind === 'sending') return;
    setOutcome({ kind: 'sending' });
    void (async () => {
      try {
        await callOperation('auth.stepup.assert', { body: { code } });
        /*
         * CLEARED BEFORE THE RETRY. The code is spent — the server records its step as the replay
         * floor — so leaving it in the field offers somebody a value that is guaranteed to be
         * refused next time.
         */
        setCode('');
        /*
         * THE CARD STAYS, AND SAYS SO. It used to close here, which is what made a successful
         * first press indistinguishable from nothing at all. `onSatisfied` runs when the person
         * closes the confirmation, and the write is still theirs to press.
         */
        setOutcome({ kind: 'accepted' });
      } catch (err: unknown) {
        if (err instanceof ConflictError) {
          // 409 — the one failure the server distinguishes, because the caller is the account
          // holder being told a fact about their own account that they can act on.
          setOutcome({ kind: 'notEnrolled' });
          return;
        }
        if (err instanceof ApiError && err.status === 401) {
          setOutcome({ kind: 'refused' });
          return;
        }
        setOutcome({
          kind: 'failed',
          requestUid: err instanceof ApiError ? err.requestUid : '',
        });
      }
    })();
  }

  /* CLOSED. One control, in the place the card was, and the screen behind it untouched. */
  if (!open) {
    return (
      <div className="stepup stepup--closed">
        <button type="button" className="btn btn--primary" onClick={() => setOpen(true)}>
          אימות זהות
        </button>
      </div>
    );
  }

  if (outcome.kind === 'accepted') {
    return (
      <div className="stepup-scrim" role="presentation" onClick={onSatisfied}>
        <div
          className="stepup-card"
          role="dialog"
          aria-modal="true"
          aria-label="אימות נוסף"
          onClick={(e) => e.stopPropagation()}
        >
          <h2 className="stepup-card__title">האימות אושר</h2>
          {/*
            AND WHAT IS STILL OUTSTANDING, NAMED. The action was refused BEFORE the factor was
            given, so nothing has been written yet. Saying "done" here would be the same silence
            in a friendlier voice.
          */}
          <p className="stepup__lead" role="status">
            {continueLabelHe === undefined
              ? 'אפשר לבצע את הפעולה שוב — היא לא בוצעה קודם, משום שהאימות נדרש לפניה.'
              : `הפעולה עצמה עדיין לא בוצעה. כדי להשלים אותה יש ללחוץ על „${continueLabelHe}".`}
          </p>
          <div className="actions-row">
            <button type="button" className="btn btn--primary" onClick={onSatisfied}>
              הבנתי — חזרה לפעולה
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (outcome.kind === 'notEnrolled') {
    return (
      <div className="stepup-scrim" role="presentation" onClick={() => setOpen(false)}>
        <div
          className="stepup-card"
          role="dialog"
          aria-modal="true"
          aria-label="אימות נוסף"
          onClick={(e) => e.stopPropagation()}
        >
          <h2 className="stepup-card__title">אימות נוסף</h2>
          <p className="stepup__lead">
            הפעולה הזו דורשת אימות דו-שלבי, ולחשבון הזה עדיין לא נרשם גורם שני — ולכן אין קוד להזין.
          </p>
          <p className="state-block__todo">יש לרשום יישומון אימות, ואז לחזור לפעולה הזו.</p>
          <div className="actions-row">
            <a className="btn btn--nav" href={routes.mfaEnrolment}>
              רישום אימות דו-שלבי
            </a>
            <button type="button" className="btn" onClick={() => setOpen(false)}>
              סגירה
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="stepup-scrim" role="presentation" onClick={() => setOpen(false)}>
      {/* The card stops the click: a press inside it must not close what it is inside. */}
      <form
        className="stepup-card"
        onSubmit={submit}
        role="dialog"
        aria-modal="true"
        aria-label="אימות נוסף"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="stepup-card__title">אימות נוסף</h2>
        {/* ONE SENTENCE. The rest is behind the help control below, on request. */}
        <p className="stepup__lead">יש להזין את הקוד מיישומון האימות כדי להשלים את הפעולה.</p>

      <div className="stepup__row">
        <label className="field">
          <span className="field__label">הקוד מהיישומון</span>
          <input
            className="field__input"
            dir="ltr"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={DIGITS}
            value={code}
            onChange={(e) => setCode(e.currentTarget.value)}
            required
          />
        </label>
        <button
          type="submit"
          className="btn btn--primary"
          disabled={outcome.kind === 'sending' || code === ''}
        >
          {outcome.kind === 'sending' ? 'בודק…' : 'אימות והמשך'}
        </button>
      </div>

      {/*
        THE FAILURE SAYS WHAT HAPPENED AND WHAT TO DO, per the standing instruction — and does NOT
        guess which refusal it was, because the server deliberately does not say.
      */}
      {outcome.kind === 'refused' ? (
        <div className="stepup__failure" role="alert">
          <p>הקוד לא התקבל. איננו יודעים אם הוא שגוי או שכבר פג.</p>
          <p className="state-block__todo">
            יש להמתין לקוד הבא ביישומון — הוא מתחלף כל 30 שניות — ולהזין אותו.
          </p>
        </div>
      ) : null}

      {outcome.kind === 'failed' ? (
        <div className="stepup__failure" role="alert">
          <p>לא הצלחנו לבדוק את הקוד, ולא ידוע לנו מה גרם לכך. הפעולה לא בוצעה.</p>
          <p className="state-block__todo">אפשר לנסות שוב. אם זה חוזר, יש לפנות לתמיכה עם הקוד שלמטה.</p>
          {outcome.requestUid === '' ? null : (
            <p className="state-block__reference">
              <span className="state-block__reference-label">קוד לפנייה לתמיכה:</span>{' '}
              <code dir="ltr">{outcome.requestUid}</code>
            </p>
          )}
        </div>
      ) : null}

      {/*
        THE DETAIL, ON REQUEST. **Section 5:** *"הוראה קצרה, שדה קוד וכפתור אישור; פירוט בעזרה לפי
        דרישה."* What was three paragraphs in everybody's way is one control that opens them.
      */}
      <button
        type="button"
        className="stepup-card__help-toggle"
        aria-expanded={helpOpen}
        onClick={() => setHelpOpen((was) => !was)}
      >
        {helpOpen ? 'סגירת ההסבר' : 'למה מבקשים ממני קוד?'}
      </button>
      {helpOpen ? (
        <div className="stepup-card__help">
          <p>
            פעולות רגישות דורשות הצגה טרייה של הגורם השני, ולא רק התחברות קודמת. הקוד ביישומון
            מתחלף כל 30 שניות.
          </p>
          <p>
            אם הזדהית לפני זמן מה, ייתכן שתתבקש שוב לקראת הפעולה עצמה — זו אותה בקרה, ולא תקלה.
            מה שהוזן במסך נשמר, והפעולה אינה נשלחת מעצמה לאחר האימות.
          </p>
        </div>
      ) : null}

      <div className="stepup-card__dismiss">
        <button type="button" className="btn" onClick={() => setOpen(false)}>
          לא עכשיו
        </button>
      </div>
      </form>
    </div>
  );
}
